import React, { useState, useRef, useEffect, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Box, Typography, Avatar, TextField, Button, CircularProgress, Drawer, Fab, Chip } from '@mui/material'
import { Person as PersonIcon, Send as SendIcon, ChevronRight as ChevronRightIcon } from '@mui/icons-material'
import { useTheme } from '@mui/material/styles'
import { checkEmpty, imageAddress } from 'data/helpers'
import { sendChatMessage, addChatMessage, resetConversation } from '/src/redux/aiJobSearch'
import TypingIndicator from './TypingIndicator'
import MessageFeedback from './MessageFeedback'
import MyStethiAIIcon from '/src/components/Icons/MyStethiAIIcon'
import { generateExampleQueries } from './helpers'

const AIChatBot = () => {
  const theme = useTheme()
  const dispatch = useDispatch()
  const [prompt, setPrompt] = useState('')
  const [drawerOpen, setDrawerOpen] = useState(true)
  const messagesEndRef = useRef(null)

  // Resizable drawer state
  const [drawerWidth, setDrawerWidth] = useState(() => {
    const saved = localStorage.getItem('aiChatDrawerWidth')
    return saved ? parseInt(saved) : 450
  })
  const [isResizing, setIsResizing] = useState(false)

  // Get user from state
  const candidate = useSelector(state => state.candidate)
  const profile = useSelector(state => state.profile?.profile)
  const { conversationId, chatHistory, isAiTyping } = useSelector(state => state.aiJobSearch)

  const exampleQueries = useMemo(
    () => generateExampleQueries(candidate),
    [candidate]
  )

  const profileImageObj = profile?.profileImage || candidate?.profileImage
  const profileImage = profileImageObj && !checkEmpty(profileImageObj.key)
    ? imageAddress(profileImageObj.key, profileImageObj.fileName)
    : null

  // Add welcome message on first load
  useEffect(() => {
    if (chatHistory.length === 0) {
      dispatch(addChatMessage({
        type: 'ai',
        message: "Search for jobs by describing what you're looking for — specialty, location, salary, benefits, or any combination.",
        metadata: { isWelcome: true }
      }))
    }
  }, [dispatch, chatHistory.length])

  // Scroll to bottom when new messages are added
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [chatHistory, isAiTyping])

  // Reset conversation on unmount
  useEffect(() => {
    return () => {
      dispatch(resetConversation())
    }
  }, [dispatch])

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen)
  }

  // Resize handlers
  const startResize = (e) => {
    const mobile = window.innerWidth < 960
    if (mobile) return
    setIsResizing(true)
    e.preventDefault()
  }

  useEffect(() => {
    if (!isResizing) return

    const handleMouseMove = (e) => {
      requestAnimationFrame(() => {
        const newWidth = window.innerWidth - e.clientX

        if (newWidth >= 350) {
          setDrawerWidth(newWidth)
        }
      })
    }

    const stopResize = () => {
      setIsResizing(false)
      localStorage.setItem('aiChatDrawerWidth', drawerWidth.toString())
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', stopResize)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', stopResize)
    }
  }, [isResizing, drawerWidth])

  // Add/remove class to body to handle main content margin
  useEffect(() => {
    const styleId = 'ai-drawer-styles'
    let styleElement = document.getElementById(styleId)

    if (!styleElement) {
      styleElement = document.createElement('style')
      styleElement.id = styleId
      document.head.appendChild(styleElement)
    }

    if (drawerOpen) {
      document.body.classList.add('ai-drawer-open')
      document.documentElement.classList.add('ai-drawer-open')

      styleElement.textContent = `
        body.ai-drawer-open main,
        body.ai-drawer-open #root,
        body.ai-drawer-open [data-testid="main-content"] {
          margin-right: ${drawerWidth}px !important;
          transition: margin-right 0.3s ease !important;
        }

        body.ai-drawer-open .MuiContainer-root:not(.ai-drawer-container) {
          margin-right: ${drawerWidth}px !important;
          transition: margin-right 0.3s ease !important;
          max-width: none !important;
        }

        @media (max-width: 959px) {
          body.ai-drawer-open main,
          body.ai-drawer-open #root,
          body.ai-drawer-open [data-testid="main-content"],
          body.ai-drawer-open .MuiContainer-root:not(.ai-drawer-container) {
            margin-right: 0 !important;
          }
        }
      `
    } else {
      document.body.classList.remove('ai-drawer-open')
      document.documentElement.classList.remove('ai-drawer-open')
      styleElement.textContent = ''
    }

    return () => {
      document.body.classList.remove('ai-drawer-open')
      document.documentElement.classList.remove('ai-drawer-open')
      const style = document.getElementById(styleId)
      if (style) {
        style.remove()
      }
    }
  }, [drawerOpen, drawerWidth])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!prompt.trim()) return

    // Add user message to chat history
    dispatch(addChatMessage({
      type: 'user',
      message: prompt
    }))

    // Send message to API
    dispatch(sendChatMessage({
      message: prompt,
      conversationId,
    }))

    // Clear input
    setPrompt('')
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit(e)
    }
  }

  const handleExampleClick = (query) => {
    dispatch(addChatMessage({ type: 'user', message: query }))
    dispatch(sendChatMessage({ message: query, conversationId }))
  }

  const hasUserMessage = chatHistory.some(m => m.type === 'user')

  return (
    <>
      {/* Stethi Toggle Button */}
      <Box
        onClick={toggleDrawer}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: drawerOpen ? drawerWidth + 24 : 24,
          zIndex: 9999,
          transition: 'right 0.3s ease',
          cursor: 'pointer',
        }}
      >
        {drawerOpen ? (
          <Fab
            aria-label="collapse Stethi"
            size="medium"
            sx={{
              backgroundColor: theme.palette.ai.main,
              '&:hover': {
                backgroundColor: theme.palette.ai.dark,
              },
              boxShadow: theme.palette.ai.shadow,
            }}
          >
            <ChevronRightIcon sx={{ color: theme.palette.common.white, fontSize: '1.5rem' }} />
          </Fab>
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              background: `linear-gradient(135deg, ${theme.palette.ai.dark} 0%, ${theme.palette.ai.main} 100%)`,
              borderRadius: '12px',
              padding: '12px 16px',
              boxShadow: theme.palette.ai.shadow,
              '&:hover': {
                boxShadow: theme.palette.shadows.heavy,
              },
              transition: 'box-shadow 0.2s ease',
            }}
          >
            <MyStethiAIIcon sx={{ color: theme.palette.common.white, fontSize: '1.3rem', mr: 1 }} />
            <Typography
              variant="button"
              sx={{
                color: theme.palette.common.white,
                fontWeight: 600,
                fontSize: '0.85rem',
                textTransform: 'none',
              }}
            >
              Stethi
            </Typography>
          </Box>
        )}
      </Box>

      {/* Stethi Drawer */}
      <Drawer
        anchor="right"
        variant="persistent"
        open={drawerOpen}
        className="ai-drawer-container"
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 400, md: drawerWidth },
            maxWidth: '100vw',
            height: '100vh',
            zIndex: 1200,
          }
        }}
      >
        {/* Resize Handle */}
        <Box
          onMouseDown={startResize}
          sx={{
            display: { xs: 'none', md: 'block' },
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: '4px',
            cursor: 'col-resize',
            backgroundColor: 'transparent',
            zIndex: 10,
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              width: '1px',
              height: '100%',
              backgroundColor: theme.palette.divider,
            },
            '&:hover, &:active': {
              backgroundColor: theme.palette.action.hover,
            },
            ...(isResizing && {
              backgroundColor: theme.palette.action.selected,
            }),
          }}
        />

        <Box
          sx={{
            height: '100vh',
            backgroundColor: theme.palette.background.default,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              borderTop: `3px solid ${theme.palette.ai.main}`,
              borderBottom: 1,
              borderColor: theme.palette.grey[200],
              backgroundColor: theme.palette.background.default,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1, color: theme.palette.ai.dark }}>
              <MyStethiAIIcon sx={{ color: theme.palette.ai.main }} />
              Stethi
            </Typography>
            <Button
              onClick={toggleDrawer}
              startIcon={<ChevronRightIcon />}
              sx={{
                color: theme.palette.grey[600],
                textTransform: 'none',
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: theme.palette.grey[100],
                }
              }}
            >
              Collapse
            </Button>
          </Box>

          {/* Messages Area */}
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            {chatHistory.map((message) => (
              <Box
                key={message.id}
                sx={{
                  display: 'flex',
                  gap: 2,
                  alignItems: 'flex-start',
                  flexDirection: message.type === 'user' ? 'row-reverse' : 'row',
                }}
              >
                {message.type === 'user' ? (
                  <Avatar
                    src={profileImage}
                    sx={{
                      bgcolor: theme.palette.primary.main,
                    }}
                  >
                    <PersonIcon />
                  </Avatar>
                ) : (
                  <Avatar
                    sx={{
                      backgroundColor: theme.palette.ai.main,
                    }}
                  >
                    <MyStethiAIIcon
                      sx={{
                        color: theme.palette.common.white,
                        fontSize: '1.3rem',
                      }}
                    />
                  </Avatar>
                )}
                <Box sx={{ display: 'flex', flexDirection: 'column', maxWidth: '80%' }}>
                  <Box
                    sx={{
                      backgroundColor: message.isError
                        ? theme.palette.error.light
                        : (message.type === 'user'
                          ? 'rgba(42, 181, 116, 0.15)'
                          : (message.metadata?.isWelcome ? `${theme.palette.ai.light}40` : theme.palette.grey[100])),
                      borderRadius: 2,
                      p: 2,
                      border: message.isError
                        ? `1px solid ${theme.palette.error.main}`
                        : (message.metadata?.isWelcome ? 'none' : 'none'),
                      borderLeft: message.metadata?.isWelcome
                        ? `3px solid ${theme.palette.ai.main}`
                        : 'none',
                    }}
                  >
                    <Typography
                      variant='body1'
                      sx={{
                        whiteSpace: 'pre-wrap',
                        color: message.isError ? theme.palette.error.dark : 'inherit'
                      }}
                    >
                      {message.message}
                    </Typography>
                    {message.jobsCount > 0 && (
                      <Typography variant='body2' sx={{ color: theme.palette.text.secondary, mt: 1 }}>
                        Found {message.jobsCount} job{message.jobsCount !== 1 ? 's' : ''}
                      </Typography>
                    )}
                  </Box>
                  {message.type === 'ai' && !message.isWelcome && !message.isError && (
                    <MessageFeedback messageId={message.id} chatbotMessageId={message.chatbotMessageId} feedback={message.feedback} conversationId={conversationId} />
                  )}
                </Box>
              </Box>
            ))}
            {isAiTyping && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  mb: 2,
                }}
              >
                <TypingIndicator />
              </Box>
            )}
            <div ref={messagesEndRef} />
          </Box>

          {/* Example Query Pills */}
          {!hasUserMessage && !isAiTyping && (
            <Box sx={{ px: 2, pb: 1, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              <Typography variant='caption' sx={{ color: theme.palette.grey[500], width: '100%', mb: 0.25 }}>
                Try:
              </Typography>
              {exampleQueries.map((query) => (
                <Chip
                  key={query}
                  label={query}
                  size='small'
                  onClick={() => handleExampleClick(query)}
                  sx={{
                    cursor: 'pointer',
                    borderColor: theme.palette.ai.main,
                    color: theme.palette.ai.dark,
                    fontWeight: 500,
                    '&:hover': {
                      backgroundColor: `${theme.palette.ai.light}40`,
                      borderColor: theme.palette.ai.dark,
                    },
                  }}
                  variant='outlined'
                />
              ))}
            </Box>
          )}

          {/* Input Area */}
          <Box
            component='form'
            onSubmit={handleSubmit}
            sx={{
              p: 2,
              borderTop: 1,
              borderColor: theme.palette.grey[300],
              backgroundColor: theme.palette.background.paper,
              borderBottomLeftRadius: 16,
              borderBottomRightRadius: 16,
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '2px',
                backgroundColor: isAiTyping ? theme.palette.ai.main : 'transparent',
                transition: 'background-color 0.3s ease',
              },
            }}
          >
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
              <TextField
                fullWidth
                multiline
                maxRows={4}
                variant='outlined'
                placeholder="Describe what you're looking for..."
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={isAiTyping}
                sx={{
                  fontFamily: '"Kumbh Sans", sans-serif',
                  '& .MuiOutlinedInput-root': {
                    fontFamily: '"Kumbh Sans", sans-serif',
                    '& input': {
                      fontFamily: '"Kumbh Sans", sans-serif',
                    },
                    '& textarea': {
                      fontFamily: '"Kumbh Sans", sans-serif',
                    },
                    '& fieldset': {
                      borderColor: isAiTyping ? theme.palette.ai.main : 'rgba(0, 0, 0, 0.23)',
                      borderWidth: isAiTyping ? '2px' : '1px',
                      transition: 'border-color 0.3s ease, border-width 0.3s ease',
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.ai.main,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.ai.main,
                    },
                    ...(isAiTyping && {
                      '& fieldset': {
                        borderColor: theme.palette.ai.main,
                        borderWidth: '2px',
                      },
                    }),
                  },
                }}
              />
              <Button
                type='submit'
                variant='contained'
                disabled={isAiTyping || !prompt.trim()}
                sx={{
                  minWidth: '56px',
                  height: '56px',
                  alignSelf: 'flex-end',
                  backgroundColor: theme.palette.ai.main,
                  '&:hover': {
                    backgroundColor: theme.palette.ai.dark,
                  },
                }}
              >
                {isAiTyping ? <CircularProgress size={24} color='inherit' /> : <SendIcon />}
              </Button>
            </Box>
          </Box>
        </Box>
      </Drawer>
    </>
  )
}

export default AIChatBot
